import { z } from 'zod';

import { parsePage, type PageResult } from '@/lib/query/page';

const rowId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const amount = z.number().min(0).max(100000000);

export const expenseWriteSchema = z.object({
  id: rowId,
  vehicleId: z
    .string()
    .max(64)
    .nullish()
    .transform((value) => (value ? value : undefined)),
  category: z.enum(['maintenance', 'assurance', 'visite', 'vignette', 'fuel', 'cleaning', 'other']),
  label: z.string().trim().min(1).max(200),
  amount,
  date: day,
});

export const fineWriteSchema = z.object({
  id: rowId,
  vehicleId: z.string().min(1).max(64),
  clientId: z.string().max(64).nullable().optional(),
  date: day,
  label: z.string().trim().min(1).max(200),
  amount,
  clientName: z.string().max(120).default(''),
  bookingId: z.string().max(64).default(''),
  status: z.enum(['unpaid', 'paid', 'disputed']).default('unpaid'),
  pvNumber: z.string().max(64).default(''),
});

export const cashWriteSchema = z.object({
  id: rowId,
  date: day,
  type: z.enum(['in', 'out']),
  category: z.enum(['avance', 'solde', 'caution_rendue', 'caution_recue', 'depense', 'autre']),
  label: z.string().trim().min(1).max(200),
  amount,
  bookingId: z.string().max(64).default(''),
});

export const expenseListSchema = z.array(expenseWriteSchema.extend({ createdAt: z.string().default('') }));
export const fineListSchema = z.array(fineWriteSchema.extend({ createdAt: z.string().default('') }));
export const cashListSchema = z.array(cashWriteSchema.extend({ createdAt: z.string().default('') }));

function rejectDuplicateIds(rows: { id: string }[], context: z.RefinementCtx, key: string): void {
  const seen = new Set<string>();
  for (const row of rows) {
    if (seen.has(row.id)) {
      context.addIssue({ code: 'custom', path: [key], message: 'Two rows use the same id.' });
      return;
    }
    seen.add(row.id);
  }
}

export const expenseReplaceSchema = z
  .object({ expenses: z.array(expenseWriteSchema).max(400) })
  .superRefine((body, context) => rejectDuplicateIds(body.expenses, context, 'expenses'));

export const fineReplaceSchema = z
  .object({ fines: z.array(fineWriteSchema).max(400) })
  .superRefine((body, context) => rejectDuplicateIds(body.fines, context, 'fines'));

export const cashReplaceSchema = z
  .object({ movements: z.array(cashWriteSchema).max(400) })
  .superRefine((body, context) => rejectDuplicateIds(body.movements, context, 'movements'));

export function ledgerWriteError(error: z.ZodError): string {
  return error.issues[0]?.message ?? 'Invalid input';
}

const expenseRowSchema = expenseWriteSchema.extend({ createdAt: z.string().default('') });
const fineRowSchema = fineWriteSchema.extend({ createdAt: z.string().default('') });
const cashRowSchema = cashWriteSchema.extend({ createdAt: z.string().default('') });

export function parseExpensePage(payload: unknown): PageResult<z.infer<typeof expenseRowSchema>> {
  return parsePage(payload, expenseRowSchema, 'The expense list');
}

export function parseFinePage(payload: unknown): PageResult<z.infer<typeof fineRowSchema>> {
  return parsePage(payload, fineRowSchema, 'The fine list');
}

export function parseCashPage(payload: unknown): PageResult<z.infer<typeof cashRowSchema>> {
  return parsePage(payload, cashRowSchema, 'The cash list');
}
